import React from 'react';
import './Statistics.scss';
import { useStatisticsVM } from './useStatisticsVM';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';
import { ContentBlock } from 'components/ContentBlock/ContentBlock';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { Loader } from 'components/Loader/Loader';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { statusMap } from 'utils/enumFormatter';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist/datalist.slice';

// Couleurs pour les camemberts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

export const Statistics: React.FunctionComponent = () => {
    const {
        filter,
        setFilter,
        users,
        extractData,
        statisticsData,
        isLoading,
        selectedYear,
        setSelectedYear,
        availableYears,
        exportAnnualStats,
        exportAllData,
        exportDataDetail,
        exportStatusCSV,
        exportAppvCSV,
        exportDepartmentCSV,
        exportFirstContactsPerDepartmentCSV,
        exportFirstMeetingsPerDepartmentCSV,
        exportPartnersCSV
    } = useStatisticsVM();
    const { datalists } = useSelector(getDatalists);

    return (
        <div className="statistics-page">
            {/* Section d'extraction des données */}
            <ContentBlock title="Extraction des prestations réalisées par les APPV">
                <div className="filter-container">
                    <div className="form">
                        <div className="form-line">
                            <div className="form-group" style={{ width: '50%' }}>
                                <label>Date de début</label>
                                <input
                                    type="date"
                                    name="startDate"
                                    placeholder="Date de début"
                                    onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
                                    value={filter.startDate}
                                />
                            </div>
                            <div className="form-group" style={{ width: '50%' }}>
                                <label>Date de fin</label>
                                <input
                                    type="date"
                                    name="endDate"
                                    placeholder="Date de fin"
                                    onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
                                    value={filter.endDate}
                                />
                            </div>
                            <div className="form-group">
                                <label>Département</label>
                                <MultiSelect
                                    name="department"
                                    isMulti={false}
                                    placeholder="Tous les départements"
                                    values={filter.department}
                                    labelKey="label"
                                    valueKey="value"
                                    update={(e: { label: string; value: string } | null) => {
                                        setFilter({
                                            ...filter,
                                            department: e
                                        });
                                    }}
                                    options={Object.entries(datalists?.departments || {}).map(([code, label]) => ({
                                        label: `${code} - ${label}`,
                                        value: code
                                    }))}
                                    colors={[]}
                                />
                            </div>
                            <div className="form-group">
                                <label>APPV(s)</label>
                                <MultiSelect
                                    name="appvs"
                                    placeholder="Selectionner des APPVs"
                                    values={filter.appvs}
                                    labelKey="label"
                                    valueKey="value"
                                    update={(e: { label: string; value: string }[]) => {
                                        setFilter({
                                            ...filter,
                                            appvs: e
                                        });
                                    }}
                                    options={users.map((user) => ({
                                        label: user.fullname,
                                        value: user.id
                                    }))}
                                    colors={[]}
                                />
                            </div>
                        </div>
                    </div>
                    <Button type="button" onClick={extractData} label="Extraire les données" />
                </div>
            </ContentBlock>

            <hr className="stats-separator" />

            <div className="year-filter-row">
                <label>Année des statistiques</label>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {availableYears.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>

            {/* Section d'export des statistiques annuelles */}
            <ContentBlock title="Export des statistiques annuelles">
                <div className="annual-stats-container">
                    <p className="annual-stats-description">
                        Exportez les statistiques annuelles du 1er janvier {selectedYear} à aujourd'hui.
                    </p>
                    <Button
                        type="button"
                        onClick={exportAnnualStats}
                        label={`Exporter les statistiques annuelles ${selectedYear}`}
                    />
                </div>
            </ContentBlock>

            {/* Section d'export complet */}
            <ContentBlock title="Export complet des données">
                <div className="annual-stats-container">
                    <p className="annual-stats-description">
                        Exportez l'ensemble des données de tous les dossiers (personnes, statuts, départements, etc.).
                    </p>
                    <Button
                        type="button"
                        onClick={exportAllData}
                        label="Exporter toutes les données"
                    />
                </div>
            </ContentBlock>

            {/* Section des statistiques */}
            <ContentBlock title="Tableau de bord statistiques">
                {isLoading ? (
                    <div className="loader-container">
                        <Loader />
                    </div>
                ) : statisticsData ? (
                    <div className="statistics-content">
                        {/* Indicateurs principaux */}
                        <div className="stats-cards">
                            <Card title="Dossiers actifs" className="stat-card">
                                <div className="stat-number primary">{statisticsData.nbPersonsIngoing}</div>
                                <div className="stat-label">Nombre de dossiers actifs</div>
                                <button className="stat-export-btn" onClick={() => exportDataDetail('active')}>Exporter</button>
                            </Card>

                            <Card title="Dossiers sortis" className="stat-card">
                                <div className="stat-number secondary">{statisticsData.nbPersonsOutgoing}</div>
                                <div className="stat-label">Nombre de dossiers sortis</div>
                                <button className="stat-export-btn" onClick={() => exportDataDetail('outgoing')}>Exporter</button>
                            </Card>

                            <Card title="Premiers contacts" className="stat-card">
                                <div className="stat-number tertiary">{statisticsData.nbFirstContacts}</div>
                                <div className="stat-label">Nombre de premiers contacts</div>
                                <button className="stat-export-btn" onClick={() => exportDataDetail('first-contacts')}>Exporter</button>
                            </Card>

                            <Card title="Premières rencontres APPV" className="stat-card">
                                <div className="stat-number tertiary">{statisticsData.nbFirstMeetings}</div>
                                <div className="stat-label">Entrées effectives dans le dispositif</div>
                            </Card>

                            <Card title="APPV" className="stat-card">
                                <div className="stat-number tertiary">{statisticsData.nbAssistants}</div>
                                <div className="stat-label">Nombre d'APPV</div>
                                <button className="stat-export-btn" onClick={() => exportDataDetail('appv')}>Exporter</button>
                            </Card>
                        </div>

                        {/* Graphiques */}
                        <div className="stats-charts">
                            <Card title="Répartition par statut" className="chart-card">
                                <div className="chart-header">
                                    <Button 
                                        type="button" 
                                        onClick={exportStatusCSV} 
                                        label="Exporter en CSV"
                                        className="export-btn"
                                    />
                                </div>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbPersonsPerStatus}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ status, count, percent }) =>
                                                    `${statusMap[status].label}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbPersonsPerStatus.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`${statusMap[data.status]?.label || data.status} : ${data.count}`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Répartition par APPV" className="chart-card">
                                <div className="chart-header">
                                    <Button 
                                        type="button" 
                                        onClick={exportAppvCSV} 
                                        label="Exporter en CSV"
                                        className="export-btn"
                                    />
                                </div>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbPersonsPerResponsiblePerson}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={false}
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbPersonsPerResponsiblePerson.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`${data.fullname || data.status} : ${data.count} dossiers`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Répartition par département" className="chart-card">
                                <div className="chart-header">
                                    <Button 
                                        type="button" 
                                        onClick={exportDepartmentCSV} 
                                        label="Exporter en CSV"
                                        className="export-btn"
                                    />
                                </div>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbPersonsPerDepartment}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ department, count, percent }) =>
                                                    `${department}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbPersonsPerDepartment.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`Département ${data.department} : ${data.count} dossiers actifs`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="File active par département" className="chart-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbPersonsIncomingPerDepartment}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ department, count, percent }) =>
                                                    `${department}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbPersonsIncomingPerDepartment.map((entry, index) => (
                                                    <Cell key={`cell-incoming-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`Département ${data.department} : ${data.count} entrées`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Premiers contacts par département" className="chart-card">
                                <div className="chart-header">
                                    <Button
                                        type="button"
                                        onClick={exportFirstContactsPerDepartmentCSV}
                                        label="Exporter en CSV"
                                        className="export-btn"
                                    />
                                </div>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbFirstContactsPerDepartment}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ department, count, percent }) =>
                                                    `${department}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbFirstContactsPerDepartment?.map((entry, index) => (
                                                    <Cell key={`cell-fc-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`Département ${data.department} : ${data.count} premiers contacts`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Premières rencontres APPV par département" className="chart-card">
                                <div className="chart-header">
                                    <Button
                                        type="button"
                                        onClick={exportFirstMeetingsPerDepartmentCSV}
                                        label="Exporter en CSV"
                                        className="export-btn"
                                    />
                                </div>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.nbFirstMeetingsPerDepartment}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ department, count, percent }) =>
                                                    `${department}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.nbFirstMeetingsPerDepartment?.map((_, index) => (
                                                    <Cell key={`cell-fm-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`Département ${data.department} : ${data.count} premières rencontres`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            <Card title="Reconnaissance MDPH par département" className="chart-card">
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.mdphPerDepartment}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ department, count, percent }) =>
                                                    `${department}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.mdphPerDepartment.map((entry, index) => (
                                                    <Cell key={`cell-mdph-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`Département ${data.department} : ${data.count} reconnaissances MDPH`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>

                            {statisticsData.nbPersonsPerStatusPerDepartment?.length > 0 && (
                                <Card title="Répartition par statut et par département" className="chart-card">
                                    <div className="status-department-table">
                                        <table className="stats-table">
                                            <thead>
                                                <tr>
                                                    <th>Département</th>
                                                    {Object.keys(statusMap).map((key) => (
                                                        <th key={key}>{statusMap[key].label}</th>
                                                    ))}
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const deptMap: Record<string, Record<string, number>> = {};
                                                    statisticsData.nbPersonsPerStatusPerDepartment.forEach((item) => {
                                                        if (!deptMap[item.department]) deptMap[item.department] = {};
                                                        deptMap[item.department][item.status] = item.count;
                                                    });
                                                    const departments = Object.keys(deptMap).sort();
                                                    const statusKeys = Object.keys(statusMap);
                                                    const totals: Record<string, number> = {};
                                                    statusKeys.forEach(k => totals[k] = 0);
                                                    let grandTotal = 0;

                                                    const rows = departments.map((dept) => {
                                                        let deptTotal = 0;
                                                        return (
                                                            <tr key={dept}>
                                                                <td><strong>{dept}</strong></td>
                                                                {statusKeys.map((key) => {
                                                                    const val = deptMap[dept][key] || 0;
                                                                    deptTotal += val;
                                                                    totals[key] += val;
                                                                    grandTotal += val;
                                                                    return <td key={key}>{val}</td>;
                                                                })}
                                                                <td><strong>{deptTotal}</strong></td>
                                                            </tr>
                                                        );
                                                    });

                                                    rows.push(
                                                        <tr key="total" className="total-row">
                                                            <td><strong>TOTAL</strong></td>
                                                            {statusKeys.map((key) => (
                                                                <td key={key}><strong>{totals[key]}</strong></td>
                                                            ))}
                                                            <td><strong>{grandTotal}</strong></td>
                                                        </tr>
                                                    );

                                                    return rows;
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            )}

                            <Card title="Partenaires par catégorie" className="chart-card">
                                <div className="chart-header">
                                    <Button
                                        type="button"
                                        onClick={exportPartnersCSV}
                                        label="Exporter en CSV"
                                        className="export-btn"
                                    />
                                </div>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <PieChart>
                                            <Pie
                                                data={statisticsData.partnersPerCategory}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ category, count, percent }) =>
                                                    `${category}: ${count} (${(percent * 100).toFixed(0)}%)`
                                                }
                                                outerRadius={120}
                                                fill="#8884d8"
                                                dataKey="count"
                                            >
                                                {statisticsData.partnersPerCategory.map((entry, index) => (
                                                    <Cell key={`cell-partners-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload[0]) {
                                                        const data = payload[0].payload;
                                                        return (
                                                            <div className="custom-tooltip">
                                                                <p>{`${data.category} : ${data.count} partenaires`}</p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </div>
                    </div>
                ) : (
                    <div className="no-data">
                        <p>Aucune donnée disponible</p>
                    </div>
                )}
            </ContentBlock>
        </div>
    );
};
